/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState, useRef, RefObject } from "react";
import { getItemByValueInDropdownOptions, isSigningAllowedForModule } from "../../../../utilities/utilityFunctions";
import { useDataContext } from "../../../DataProvider/DataContext";
import { SelectField } from "../../formFields/SelectField";
import { Accordion, AccordionDetails, AccordionSummary, ButtonBase } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import palette from "../../../../utilities/palette";
import { TextField } from "../../formFields/TextField";
import { MultiValueTextField } from "../../formFields/MultiValuesTextField";
import {  actionTypeOptions, f163D2SpecVersionOptions, programmingMethodOptions, swDownloadMethodOptions, trueFalseDropdownOptions, versionChangeLevelOptions } from "../../../../utilities/dropDownOptions";
import { SwSigningDetails } from "./SwSigningDetails";
import { SelectionCriteria } from "./SelectionCriteria";
import { checkIfFieldIsRequiredPerArchitecture } from "../../../../utilities/architectureTypeDataProcess";
import { validateClusterValue } from "../../../../utilities/formValidation";
import Version from "../../../../utilities/Version";
import ClusterUrlSelector from "./ClusterSelector";
import ReorderingClusterButtons from "./ReorderingClusterButtons";
import { FlexColumnDiv, FlexRowDiv, StepperContentBodyDiv } from "../../../styledComponents/Components.styles";
import LabelField from "../../formFields/LabelField";
import { textHints } from "../../../../utilities/hintText";
import { RemoveButton } from "../../../styledComponents/Buttons.styles";
import { ECUDataInterface } from "../../../../models/ECUData";
import { extractClusterErrors } from "../../../../utilities/validationErrorExtraction";
import SwPartNumberLabelField from "../../formFields/SwPartNumberLabelField";
import Part2Selector from "./Part2Selector";

const updateMachineListCluster = (prevState, machineIndex, variantIndex, clusterIndex, field, value) => {
    const newState = [...prevState];
    newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex][field] = value;
    return newState;
};

const getECUDataValue = (ECUData: RefObject<ECUDataInterface>, machineIndex, variantIndex, clusterIndex, field) => {
    return ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex][field];
};



export const ClusterList = ({
    moduleInformation,
    machineList, setMachineList, machineIndex,
    machineListErrors, setMachineListErrors,
    variantIndex, projectKey, moduleName
}) => {

    const {architectureType, ECUData, setECUData, importProcessData, signingAllowedModules} = useDataContext();
    // tracks/handles what accordion element is expanded
    const [expanded, setExpanded] = useState(``);

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };


    
    const { fieldsWithErrorList, criteriaWithErrorList } = extractClusterErrors(machineListErrors, machineIndex, variantIndex);

    useEffect(() => {
        // Quick hack to resolve the issue where it's not possible to properly re-sync value for fields 
        setECUData((prevState) => {
            console.log("Current ECUData in ClusterList useEffect:", prevState);
            return structuredClone(prevState);
        });
    }, []);


    const updateVersionFromChangeLevel = (changeLevel, originalVersion) => {
        const version = new Version(originalVersion);
        switch(changeLevel) {
            case "MAJOR":
                version.revMajor();
                break;
            case "MINOR":
                version.revMinor();
                break;
            case "PATCH":
                version.revPatch();
                break;
            case "NO_CHANGE":
                break;
            default:
                break;
        }
        return version.version;
    };

    const handleChangeLevelChange = (selectedOption, machineIndex, variantIndex, clusterIndex) => {
        const changeLevel = selectedOption.value;
        setMachineList(prevState => {
            const newState = updateMachineListCluster(prevState, machineIndex, variantIndex, clusterIndex, 'changeLevel', changeLevel);
            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].changeLevel = changeLevel;
            if (["FNV2", "FNV3"].includes(architectureType) && !checkIfClusterIsImported(clusterIndex)) {
                const originalVersion = getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'clusterVersion');
                const newVersion = updateVersionFromChangeLevel(changeLevel, originalVersion);
                newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].clusterVersion = newVersion;
                validateClusterValue(machineIndex, variantIndex, clusterIndex, 'clusterVersion', newVersion, setMachineListErrors);
            }
            validateClusterValue(machineIndex, variantIndex, clusterIndex, 'changeLevel', changeLevel, setMachineListErrors);
            return newState;
        });
    };

    const checkIfClusterIsImported = (clusterIndex) => {
        if (!importProcessData.enabled) return false;
        const clusterName = machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].clusterName;
        const addedClusters = importProcessData.addedClusters || [];
        const matchingAddedCluster = addedClusters.find((addedCluster) => addedCluster.clusterName === clusterName && addedCluster.machineIndex === machineIndex && addedCluster.variantIndex === variantIndex);    
        return !!matchingAddedCluster;
    }



    // Generic handlers for common field operations
    const updateFieldValue = (machineIndex, variantIndex, clusterIndex, fieldName, value, pathForValidation, forceValidation = false) => {
        setMachineList(prevState => updateMachineListCluster(prevState, machineIndex, variantIndex, clusterIndex, fieldName, value));

        if (forceValidation || checkIfFieldIsRequiredPerArchitecture(pathForValidation, architectureType)) {
            validateClusterValue(machineIndex, variantIndex, clusterIndex, fieldName, value, setMachineListErrors);
        }
    };

    const createFieldChangeHandler = (machineIndex, variantIndex, clusterIndex, fieldName, pathForValidation) => (e) => {
        updateFieldValue(machineIndex, variantIndex, clusterIndex, fieldName, e.target.value, pathForValidation);
    };

    const createSelectFieldChangeHandler = (machineIndex, variantIndex, clusterIndex, fieldName, pathForValidation) => (selectedOption) => {
        updateFieldValue(machineIndex, variantIndex, clusterIndex, fieldName, selectedOption.value, pathForValidation);
    };

    const createFieldRevertHandler = (machineIndex, variantIndex, clusterIndex, fieldName, pathForValidation) => () => {
        const originalValue = getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, fieldName);
        updateFieldValue(machineIndex, variantIndex, clusterIndex, fieldName, originalValue, pathForValidation);
    };

    const parseIntegerValue = (e, defaultValue = 0) => {
        if (e.target.validity.valid) {
            return parseInt(e.target.value, 10);
        }
        return defaultValue;
    };

    const createIntFieldChangeHandler = (machineIndex, variantIndex, clusterIndex, fieldName, pathForValidation) => (e) => {
        const value = parseIntegerValue(e, 0);
        updateFieldValue(machineIndex, variantIndex, clusterIndex, fieldName, value, pathForValidation);
    };



    const handleRemoveButtonClick = (e, clusterIndex) => {
        e.stopPropagation();
        setExpanded(``);
        setMachineList(prevState => {
            const newState = structuredClone(prevState);
            newState[machineIndex].variantList[variantIndex].clusterList.splice(clusterIndex, 1);
            return newState;
        });
        setMachineListErrors(prevState => {
            const newState = structuredClone(prevState);
            newState[machineIndex].variantListErrors[variantIndex].clusterListErrors.splice(clusterIndex, 1);
            return newState;
        });

        setECUData((prevState) => {
            const newState = structuredClone(prevState);
            newState.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList.splice(clusterIndex, 1);
            return newState;
        });
    }

    const lastSequence = useRef(0);
    return (
        <> 
        <div style={{color: palette.main, fontWeight: '600', fontSize: '24px', margin: '20px 0'}}>Clusters Information</div>
            {machineList[machineIndex].variantList[variantIndex].clusterList.map((clusterEntry, clusterIndex) => 
            {
                const showSequenceHeader = clusterIndex === 0 ||
                    clusterEntry.swInstallationSequence !== lastSequence.current;
                lastSequence.current = clusterEntry.swInstallationSequence;
                
                return( 
                <div 
                    key={`clusterList-${clusterEntry.clusterName}_${clusterEntry.clusterVersion}`}
                    style={{ 
                        margin: '10px'
                    }}>
                    {showSequenceHeader &&<div
                        style={{
                            fontWeight: '700',
                            fontSize: '18px',
                            color: 'grey',
                            marginBottom: '10px'
                        }}
                    >{`Installation Sequence ${clusterEntry.swInstallationSequence}`}</div>}

                    <Accordion 
                        expanded={expanded === `panel${machineIndex}-${variantIndex}-${clusterIndex}`} 
                        onChange={handleChange(`panel${machineIndex}-${variantIndex}-${clusterIndex}`)}
                        id={`panel${machineIndex}-${variantIndex}-${clusterIndex}`}
                        slotProps={{ transition: { unmountOnExit: true } }}
                        sx={{ 
                            "::before": {
                                display: 'none'
                            },
                            background: 'transparent',
                            boxShadow: 'none',
                        }}
                    >
                        {/* Top bar of accordion element */}
                        <ButtonBase sx={{width: '100%'}}>
                            <AccordionSummary
                                aria-controls={`panel${machineIndex}-${variantIndex}-${clusterIndex}-content`}
                                id={`panel${machineIndex}-${variantIndex}-${clusterIndex}-header`}
                                sx={{
                                    ":hover": {
                                        backgroundColor: palette.header.main
                                    },
                                    borderRadius: '10px'
                                }}
                            >
                                {expanded === `panel${machineIndex}-${variantIndex}-${clusterIndex}` && <ExpandMoreIcon sx={{fontSize: '30px'}}/>}



                                <FlexRowDiv style={{
                                     width: "100%", cursor: 'pointer',                                        borderLeft: `4px solid ${palette.main}`,
                                        borderRadius: '4px',
                                        // minHeight: '100px',
                                        padding: '10px 20px 10px 20px',
                                        // gap: '5px',
                                }}>

                                <ReorderingClusterButtons
                                    machineList={machineList}
                                    setMachineList={setMachineList}
                                    setMachineListErrors={setMachineListErrors}
                                    machineIndex={machineIndex}
                                    variantIndex={variantIndex}
                                    clusterIndex={clusterIndex}
                                />

                                    <FlexColumnDiv style={{
                                        padding: '10px 20px 10px 20px',
                                        gap: '5px',
                                        width: '100%',
                                        minHeight: '116px'
                                    }}>
                                        <span style={{ fontWeight: "650", fontSize: "20px", paddingLeft: '5px', color: palette.main }}>
                                            Cluster {`${clusterIndex + 1}`}
                                        </span>
                                        <FlexRowDiv style={{gap: '100px'}}>
                                            <FlexColumnDiv style={{gap: '5px', width: '412px'}}>
                                                <LabelField
                                                    label={"Cluster Name"}
                                                    value={clusterEntry.clusterName}
                                                    cursor="pointer"
                                                />
                                                <LabelField
                                                    label={"Cluster Type"}
                                                    value={clusterEntry.clusterType}
                                                    cursor="pointer"
                                                />
                                            </FlexColumnDiv>
                                            <FlexColumnDiv style={{gap: '5px'}}>
                                                <LabelField
                                                    label={"Cluster Version"}
                                                    value={clusterEntry.clusterVersion || "N/A"}
                                                    cursor="pointer"
                                                />
                                                {isSigningAllowedForModule(signingAllowedModules, moduleName) &&
                                                    <SwPartNumberLabelField
                                                        value={clusterEntry.swPartNumber || "N/A"}
                                                        errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.swPartNumberValidationError || ""}
                                                        cursor="pointer"
                                                        isLoading={clusterEntry.isProcessingSignFileMetadata === true}
                                                    />
                                                }
                                            </FlexColumnDiv>
                                        </FlexRowDiv>
                                    


                                    {fieldsWithErrorList[clusterIndex] && fieldsWithErrorList[clusterIndex].length > 0 &&
                                        <span style={{color: palette.stepper.error, paddingTop: '5px', marginLeft: '5px'}}>Validation errors found in one or more input fields</span>
                                    }
                                    {criteriaWithErrorList[clusterIndex] && criteriaWithErrorList[clusterIndex].length > 0 &&
                                        <span style={{color: palette.stepper.error, paddingTop: '5px', marginLeft: '5px'}}>Error in {criteriaWithErrorList[clusterIndex]?.join(", ")}</span>
                                    }
                                    </FlexColumnDiv>     

                                        <RemoveButton
                                            style={{alignSelf: 'center '}}
                                            disabled={machineList[machineIndex].variantList[variantIndex].clusterList.length <= 1}
                                            onClick={(e) => handleRemoveButtonClick(e, clusterIndex)}
                                            >X</RemoveButton>                           
                                
                                </FlexRowDiv>



                            </AccordionSummary>
                        </ButtonBase>

                        <AccordionDetails
                            sx={{
                                marginLeft: '10px',
                            }}
                        >   
                            <FlexColumnDiv>

                            <div style={{color: palette.main, fontWeight: '600', fontSize: '20px', margin: '20px 0'}}>Software Cluster Attributes</div>

                            <StepperContentBodyDiv style={{width: '1240px', alignSelf: 'center', marginBottom: '20px', alignItems: 'center', flexDirection: 'column'}}>
                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <SelectField
                                            label="Cluster Change Level"
                                            options={versionChangeLevelOptions}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.changeLevel`, architectureType)}
                                            value={getItemByValueInDropdownOptions(versionChangeLevelOptions, clusterEntry.changeLevel)}
                                            placeholder="Select Change Level"
                                            textHint={textHints.changeLevel}
                                            onChange={(selectedOption) => handleChangeLevelChange(selectedOption, machineIndex, variantIndex, clusterIndex)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.changeLevelValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.changeLevel`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.changeLevel !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'changeLevel')}
                                        />
                                        <MultiValueTextField
                                            label={"F113 ECU Assembly Number"}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.f113ECUAssemblyNum`, architectureType)}
                                            value={clusterEntry.f113ECUAssemblyNum.map((item) => ({ value: item, label: item }))}
                                            placeholder="Enter values (press Enter after each)"
                                            onChange={(selectedOptions) => {
                                                const selectedValues = Array.from(new Set(selectedOptions.map(option => option.value)));
                                                updateFieldValue(machineIndex, variantIndex, clusterIndex, 'f113ECUAssemblyNum', selectedValues, `machineList.0.variantList.0.clusterList.0.f113ECUAssemblyNum`);
                                            }}
                                            onRevert={() => {
                                                const originalValue = getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'f113ECUAssemblyNum');
                                                updateFieldValue(machineIndex, variantIndex, clusterIndex, 'f113ECUAssemblyNum', originalValue, `machineList.0.variantList.0.clusterList.0.f113ECUAssemblyNum`);
                                            }}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.f113ECUAssemblyNumValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.f113ECUAssemblyNum`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.f113ECUAssemblyNum !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'f113ECUAssemblyNum')}
                                        />
                                    </FlexRowDiv>
                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <ClusterUrlSelector
                                            clusterEntry={clusterEntry}
                                            machineList={machineList}
                                            setMachineList={setMachineList}
                                            machineIndex={machineIndex}
                                            variantIndex={variantIndex}
                                            clusterIndex={clusterIndex}
                                            setMachineListErrors={setMachineListErrors}
                                            machineListErrors={machineListErrors}
                                            projectKey={projectKey}
                                            moduleName={moduleName}
                                        />

                                        <MultiValueTextField
                                            label={"F111 Core Assembly Number"}
                                            value={clusterEntry.f111CoreAssemblyNum.map((item) => ({ value: item, label: item }))}
                                            placeholder="Enter values (press Enter after each)"
                                            textHint={textHints.f111coreAssemblyNum}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.f111CoreAssemblyNum`, architectureType)}
                                            onChange={(selectedOptions) => {
                                                const selectedValues = Array.from(new Set(selectedOptions.map(option => option.value)));
                                                updateFieldValue(machineIndex, variantIndex, clusterIndex, 'f111CoreAssemblyNum', selectedValues, `machineList.0.variantList.0.clusterList.0.f111CoreAssemblyNum`);
                                            }}

                                            onRevert={() => {
                                                const originalValue = getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'f111CoreAssemblyNum');
                                                updateFieldValue(machineIndex, variantIndex, clusterIndex, 'f111CoreAssemblyNum', originalValue, `machineList.0.variantList.0.clusterList.0.f111CoreAssemblyNum`);
                                            }}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.f111CoreAssemblyNumValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.f111CoreAssemblyNum`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={JSON.stringify(clusterEntry.f111CoreAssemblyNum) !== JSON.stringify(getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'f111CoreAssemblyNum'))}
                                        />
                                    </FlexRowDiv>
                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <SelectField
                                            label="F163 D2 Spec Version"
                                            options={f163D2SpecVersionOptions}
                                            textHint={textHints.f163D2SpecVersion}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.f163D2SpecVersion`, architectureType)}
                                            value={getItemByValueInDropdownOptions(f163D2SpecVersionOptions, clusterEntry.f163D2SpecVersion)}
                                            placeholder="Select F163 D2 Spec Version"
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'f163D2SpecVersion', `machineList.0.variantList.0.clusterList.0.f163D2SpecVersion`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'f163D2SpecVersion', `machineList.0.variantList.0.clusterList.0.f163D2SpecVersion`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.f163D2SpecVersionValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.f163D2SpecVersion`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.f163D2SpecVersion !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'f163D2SpecVersion')}
                                        />

                                        <TextField
                                            label={"Update Time in Seconds"}
                                            value={clusterEntry.updateTimeInSeconds}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.updateTimeInSeconds`, architectureType)}
                                            placeholder={`Enter Update Time in Seconds`}
                                            isInt={true}
                                            textHint={textHints.updateTimeInSeconds}
                                            onChange={createIntFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'updateTimeInSeconds', `machineList.0.variantList.0.clusterList.0.updateTimeInSeconds`)}
                                            onRevert={() => {
                                                const originalValue = getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'updateTimeInSeconds');
                                                updateFieldValue(machineIndex, variantIndex, clusterIndex, 'updateTimeInSeconds', originalValue, `machineList.0.variantList.0.clusterList.0.updateTimeInSeconds`);
                                            }}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.updateTimeInSecondsValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.updateTimeInSeconds`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.updateTimeInSeconds !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'updateTimeInSeconds')}
                                        />  
                                    </FlexRowDiv>
                                    
                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <Part2Selector
                                            clusterEntry={clusterEntry}
                                            machineList={machineList}
                                            setMachineList={setMachineList}
                                            machineIndex={machineIndex}
                                            variantIndex={variantIndex}
                                            clusterIndex={clusterIndex}
                                            setMachineListErrors={setMachineListErrors}
                                            machineListErrors={machineListErrors}
                                            projectKey={projectKey}
                                            moduleName={moduleName}
                                        />
                                        <SelectField
                                            label={"Inhibit Required"}
                                            options={trueFalseDropdownOptions}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.inhibitRequired`, architectureType)}
                                            value={getItemByValueInDropdownOptions(trueFalseDropdownOptions, clusterEntry.inhibitRequired)}
                                            prefilledValue={ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].inhibitRequired}
                                            textHint={textHints.inhibitRequired}
                                            placeholder="Select Inhibit Required"
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'inhibitRequired', `machineList.0.variantList.0.clusterList.0.inhibitRequired`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'inhibitRequired', `machineList.0.variantList.0.clusterList.0.inhibitRequired`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.inhibitRequiredValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.inhibitRequired`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={JSON.stringify(clusterEntry.inhibitRequired) !== JSON.stringify(ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].inhibitRequired)}
                                        />
                                    </FlexRowDiv>
                                    
                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <TextField
                                            label={"WERS Concern Number"}
                                            value={clusterEntry.wersConcernNum}
                                            placeholder={`Enter WERS Concern Number`}
                                            textHint={textHints.wersConcernNum}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.wersConcernNum`, architectureType)}
                                            onChange={createFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'wersConcernNum', `machineList.0.variantList.0.clusterList.0.wersConcernNum`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'wersConcernNum', `machineList.0.variantList.0.clusterList.0.wersConcernNum`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.wersConcernNumValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.wersConcernNum`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.wersConcernNum !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'wersConcernNum')}
                                        />
                                        <SelectField
                                            label={"SW Download Method"}
                                            options={swDownloadMethodOptions}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.swDownloadMethod`, architectureType)}
                                            value={getItemByValueInDropdownOptions(swDownloadMethodOptions, clusterEntry.swDownloadMethod)}
                                            textHint={textHints.swDownloadMethod}
                                            placeholder="Select SW Download Method"
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'swDownloadMethod', `machineList.0.variantList.0.clusterList.0.swDownloadMethod`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'swDownloadMethod', `machineList.0.variantList.0.clusterList.0.swDownloadMethod`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.swDownloadMethodValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.swDownloadMethod`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.swDownloadMethod !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'swDownloadMethod')}
                                        />
                                    </FlexRowDiv>
                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <SelectField
                                            label="Programming Method"
                                            options={programmingMethodOptions}
                                            value={getItemByValueInDropdownOptions(programmingMethodOptions, clusterEntry.programmingMethod)}
                                            placeholder="Select Programming Method"
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'programmingMethod', `machineList.0.variantList.0.clusterList.0.programmingMethod`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'programmingMethod', `machineList.0.variantList.0.clusterList.0.programmingMethod`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.programmingMethodValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.programmingMethod`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.programmingMethod !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'programmingMethod')}
                                            textHint={textHints.programmingMethod}
                                        />
                                        <SelectField
                                            label="Assembly Critical"
                                            options={trueFalseDropdownOptions}
                                            required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.assemblyCritical`, architectureType)}
                                            value={getItemByValueInDropdownOptions(trueFalseDropdownOptions, clusterEntry.assemblyCritical)}
                                            placeholder="Select Assembly Critical"
                                            textHint={textHints.assemblyCritical}
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'assemblyCritical', `machineList.0.variantList.0.clusterList.0.assemblyCritical`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'assemblyCritical', `machineList.0.variantList.0.clusterList.0.assemblyCritical`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.assemblyCriticalValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.assemblyCritical`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.assemblyCritical !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'assemblyCritical')}
                                        />
                                    </FlexRowDiv>

                                    <FlexRowDiv style={{gap: '36px'}}>
                                        <SelectField
                                            label="Action Type"
                                            options={actionTypeOptions}
                                            value={getItemByValueInDropdownOptions(actionTypeOptions, clusterEntry.actionType)}
                                            placeholder="Select Action Type"
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'actionType', `machineList.0.variantList.0.clusterList.0.actionType`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'actionType', `machineList.0.variantList.0.clusterList.0.actionType`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.actionTypeValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.actionType`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.actionType !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'actionType')}
                                            textHint={textHints.actionType}
                                        />
                                        <SelectField
                                            label="Production Release Intent"
                                            options={trueFalseDropdownOptions}
                                            value={getItemByValueInDropdownOptions(trueFalseDropdownOptions, clusterEntry.productionReleaseIntent)}
                                            placeholder="Select Production Release Intent"
                                            textHint={textHints.productionReleaseIntent}
                                            onChange={createSelectFieldChangeHandler(machineIndex, variantIndex, clusterIndex, 'productionReleaseIntent', `machineList.0.variantList.0.clusterList.0.productionReleaseIntent`)}
                                            onRevert={createFieldRevertHandler(machineIndex, variantIndex, clusterIndex, 'productionReleaseIntent', `machineList.0.variantList.0.clusterList.0.productionReleaseIntent`)}
                                            errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.productionReleaseIntentValidationError}
                                            pathToField={`machineList.0.variantList.0.clusterList.0.productionReleaseIntent`}
                                            stylingVariant="body"
                                            isPrefilledValueChanged={clusterEntry.productionReleaseIntent !== getECUDataValue(ECUData, machineIndex, variantIndex, clusterIndex, 'productionReleaseIntent')}
                                        />
                                    </FlexRowDiv>
                                    { !isSigningAllowedForModule(signingAllowedModules, moduleName) && 
                                        <FlexRowDiv style={{gap: '36px'}}>
                                            <TextField
                                                label={"Software Part Number"}
                                                value={clusterEntry.swPartNumber}
                                                placeholder={`Enter Software Part Number`}
                                                textHint={textHints.swPartNumber}
                                                required={checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.swPartNumber`, architectureType)}
                                                onChange={(e) => {
                                                    setMachineList(
                                                        (prevState) => {
                                                            const newState = [...prevState];
                                                            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber = e.target.value;
                                                            return newState;
                                                        }
                                                    );
                                                    if (checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.swPartNumber`, architectureType)) {
                                                        validateClusterValue(
                                                            machineIndex,
                                                            variantIndex,
                                                            clusterIndex,
                                                            'swPartNumber',
                                                            e.target.value,
                                                            setMachineListErrors
                                                        );
                                                    }
                                                } }
                                                onRevert={(e) => {
                                                    setMachineList(
                                                        (prevState) => {
                                                            const newState = [...prevState];
                                                            newState[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber = ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber;
                                                            return newState;
                                                        }
                                                    );
                                                    if (checkIfFieldIsRequiredPerArchitecture(`machineList.0.variantList.0.clusterList.0.swPartNumber`, architectureType)) {
                                                        validateClusterValue(
                                                            machineIndex,
                                                            variantIndex,
                                                            clusterIndex,
                                                            'swPartNumber',
                                                            ECUData.current.prefilledJSONContent.machineList[machineIndex].variantList[variantIndex].clusterList[clusterIndex].swPartNumber,
                                                            setMachineListErrors
                                                        );
                                                    }
                                                } }
                                                errorMessage={machineListErrors[machineIndex]?.variantListErrors[variantIndex]?.clusterListErrors[clusterIndex]?.swPartNumberValidationError}
                                                pathToField={`machineList.0.variantList.0.clusterList.0.swPartNumber`}
                                                stylingVariant='body'
                                            />
                                            <div style={{width: "560px"}}></div>
                                        </FlexRowDiv>
                                    }
                                    
                            </StepperContentBodyDiv>

                            <SwSigningDetails
                                moduleName={moduleName}
                                machineName={machineList[machineIndex].machineName}
                                canDiagRxId={machineList[machineIndex].canDiagRxId}
                                variantName={machineList[machineIndex].variantList[variantIndex].variantName}
                                clusterEntry={clusterEntry}
                                machineIndex={machineIndex}
                                variantIndex={variantIndex}
                                clusterIndex={clusterIndex}
                                machineListErrors={machineListErrors}
                                setMachineListErrors={setMachineListErrors}
                                setMachineList={setMachineList}
                            />

                            <SelectionCriteria
                                moduleInformation={moduleInformation}
                                machineList={machineList}
                                setMachineList={setMachineList}
                                machineIndex={machineIndex}
                                variantIndex={variantIndex}
                                clusterIndex={clusterIndex}
                                machineListErrors={machineListErrors}
                                setMachineListErrors={setMachineListErrors}
                            />
                            </FlexColumnDiv>
                        </AccordionDetails>
                    </Accordion>
                </div>
            )})}
        </>

    )
}

